// features/environment/scope1Emission/Scope1EmissionModal.jsx

import { useState, useEffect } from "react";
import apiClient from "../../../shared/API/apiClient";

const EMPTY_FORM = {
  record_date: "",
  department_id: "",
  site_id: "",
  fuel_type_id: "",
  quantity: "",
  unit: "KL",
  emission_factor: "",
  tco2e: "",
};

// ── same validation pattern as OrganizationProfile ────────────────────────
const VALIDATIONS = {
  record_date: { required: true, label: "Record Date" },
  department_id: { required: true, label: "Department" },
  site_id: { required: true, label: "Site" },
  fuel_type_id: { required: true, label: "Fuel Type" },
  quantity: { required: true, label: "Quantity" },
};

const validate = (form) => {
  const errors = {};

  for (const [field, rules] of Object.entries(VALIDATIONS)) {
    const value = String(form[field] ?? "").trim();

    if (rules.required && !value) {
      errors[field] = `${rules.label} is required`;
      continue;
    }

    if (field === "quantity" && value && Number(value) <= 0) {
      errors[field] = "Quantity must be greater than 0";
    }
  }

  return errors;
};

const Scope1EmissionModal = ({
  onClose,
  onSaved,
  editRecord,
  reportingYear,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [departments, setDepts] = useState([]);
  const [fuelTypes, setFuels] = useState([]);
  const [sites, setSites] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingSites, setLoadingSites] = useState(false);
  const [status, setStatus] = useState("idle");
  const [apiError, setApiError] = useState("");

  // ── on mount: fetch dropdowns, then resolve editRecord names → ids ─────────
  useEffect(() => {
    const init = async () => {
      try {
        const org_id = localStorage.getItem("org_id") ?? 2;

        const listRes = await apiClient.get(
          `/api/fueldept-list?org_id=${org_id}`
        );
        const listData = listRes.data;
        let depts = [];
        let fuels = [];

        if (listData.status === "success" || listData.code === 200) {
          depts = listData.data?.departments ?? [];
          fuels = listData.data?.fuel_types ?? [];
          setDepts(depts);
          setFuels(fuels);
        }

        if (editRecord) {
          const matchedDept = depts.find(
            (d) => d.name === editRecord.department_name
          );
          const dept_id = matchedDept ? String(matchedDept.id) : "";

          const matchedFuel = fuels.find(
            (f) => f.name === editRecord.fuel_type
          );
          const fuel_type_id = matchedFuel ? String(matchedFuel.id) : "";

          let site_id = "";
          if (dept_id) {
            const siteRes = await apiClient.get("/api/site-list", {
              params: { org_id, dept_id },
            });
            const siteData = siteRes.data;
            if (siteData.status === "success" || siteData.code === 200) {
              const siteList = siteData.data ?? [];
              setSites(siteList);
              const matchedSite = siteList.find(
                (s) => s.site_name === editRecord.site_name
              );
              site_id = matchedSite ? String(matchedSite.site_id) : "";
            }
          }

          const ef = editRecord.emission_factor ?? "";
          const qty = editRecord.quantity ?? "";
          const tco2e = ef && qty ? (Number(ef) * Number(qty)).toFixed(2) : "";

          setForm({
            record_date: editRecord.record_date ?? "",
            department_id: dept_id,
            site_id,
            fuel_type_id,
            quantity: String(qty),
            unit: editRecord.unit ?? "KL",
            emission_factor: String(ef),
            tco2e,
          });
        }
      } catch (err) {
        console.error("Modal init failed:", err);
      } finally {
        setLoadingList(false);
      }
    };

    init();
  }, []);

  // ── department change → fetch sites ───────────────────────────────────────
  const handleDeptChange = async (e) => {
    const dept_id = e.target.value;
    setApiError("");
    setForm((prev) => ({ ...prev, department_id: dept_id, site_id: "" }));
    if (errors.department_id)
      setErrors((prev) => ({ ...prev, department_id: undefined }));
    if (errors.site_id) setErrors((prev) => ({ ...prev, site_id: undefined }));
    setSites([]);
    if (!dept_id) return;

    setLoadingSites(true);
    try {
      const org_id = localStorage.getItem("org_id") ?? 2;
      const response = await apiClient.get("/api/site-list", {
        params: { org_id, dept_id },
      });
      const data = response.data;
      if (data.status === "success" || data.code === 200) {
        setSites(data.data ?? []);
      }
    } catch (err) {
      console.error("Failed to fetch sites:", err);
    } finally {
      setLoadingSites(false);
    }
  };

  // ── fuel type change → auto-fill emission factor, recalc tco2e ────────────
  const handleFuelChange = (e) => {
    setApiError("");
    const fuel_type_id = e.target.value;
    const matched = fuelTypes.find((f) => String(f.id) === fuel_type_id);
    const ef = matched?.emission_factor ?? "";
    const qty = form.quantity;
    const tco2e = ef && qty ? (Number(ef) * Number(qty)).toFixed(2) : "";

    setForm((prev) => ({
      ...prev,
      fuel_type_id,
      emission_factor: String(ef),
      tco2e,
    }));
    if (errors.fuel_type_id)
      setErrors((prev) => ({ ...prev, fuel_type_id: undefined }));
    if (status !== "idle") setStatus("idle");
  };

  // ── quantity change → recalc tco2e ────────────────────────────────────────
  const handleQuantityChange = (e) => {
    setApiError("");
    const qty = e.target.value;
    const ef = form.emission_factor;
    const tco2e = ef && qty ? (Number(ef) * Number(qty)).toFixed(2) : "";

    setForm((prev) => ({ ...prev, quantity: qty, tco2e }));
    if (errors.quantity)
      setErrors((prev) => ({ ...prev, quantity: undefined }));
    if (status !== "idle") setStatus("idle");
  };

  // ── same handleChange + handleBlur pattern as OrganizationProfile ─────────
  const handleChange = (field) => (e) => {
    setApiError("");
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    if (status !== "idle") setStatus("idle");
  };

  const handleBlur = (field) => () => {
    const fieldErrors = validate({ ...form });
    if (fieldErrors[field]) {
      setErrors((prev) => ({ ...prev, [field]: fieldErrors[field] }));
    }
  };

  // ── submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const fieldErrors = validate(form);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    setStatus("loading");
    setApiError("");

    try {
      const org_id = Number(localStorage.getItem("org_id") ?? 2);
      const payload = {
        id: editRecord?.id ?? 0,
        org_id,
        reporting_year: reportingYear, 
        record_date: form.record_date,
        department_id: Number(form.department_id),
        site_id: Number(form.site_id),
        fuel_type_id: Number(form.fuel_type_id),
        quantity: Number(form.quantity),
        unit: form.unit,
        emission_factor: form.emission_factor
          ? parseFloat(form.emission_factor)
          : undefined,
        tco2e: form.tco2e ? parseFloat(form.tco2e) : undefined,
      };

      const response = await apiClient.post("/api/scope1-addedit", payload);
      const data = response.data;

      if (data.status === "success" || data.code === 200) {
        onSaved(data.data ?? payload, !!editRecord);
      } else {
        throw new Error(data.message || "Save failed");
      }
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Something went wrong.";
      setApiError(msg);
      setStatus("error");
    }
  };

  const isLoading = status === "loading";
  const isEdit = !!editRecord;
  const showEfAndTco2e = !!form.fuel_type_id;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            {isEdit ? "Edit Entry" : "Add Entry"}
          </div>
          <button
            className="modal-close"
            onClick={onClose}
            disabled={isLoading}
          >
            ✕
          </button>
        </div>

        <div className="modal-body">

          {/* Reporting Year */}
          {/* <div className="org-form-row" style={{ gridTemplateColumns: "1fr" }}>
            <div className="org-form-field">
              <label className="org-field-label">Reporting Year</label>
              <input
                className="org-field-input"
                type="text"
                value={reportingYear || ""}
                disabled
              />
            </div>
          </div> */}

          {/* Record Date */}
          <div className="org-form-row" style={{ gridTemplateColumns: "1fr" }}>
            <div className="org-form-field">
              <label className="org-field-label">
                Record Date <span className="req">*</span>
              </label>
              <input
                className={`org-field-input ${
                  errors.record_date ? "org-field-input--error" : ""
                }`}
                type="date"
                value={form.record_date}
                onChange={handleChange("record_date")}
                onBlur={handleBlur("record_date")}
                disabled={isLoading}
                max={new Date().toISOString().split("T")[0]}
              />
              {errors.record_date && (
                <span className="org-field-error">{errors.record_date}</span>
              )}
            </div>
          </div>

          {/* Department — dropdown */}
          <div className="org-form-row" style={{ gridTemplateColumns: "1fr" }}>
            <div className="org-form-field">
              <label className="org-field-label">
                Department <span className="req">*</span>
              </label>
              {loadingList ? (
                <select className="org-field-input" disabled>
                  <option>Loading departments…</option>
                </select>
              ) : departments.length === 0 ? (
                <select className="org-field-input" disabled>
                  <option>Add departments in Administration first</option>
                </select>
              ) : (
                <select
                  className={`org-field-input ${
                    errors.department_id ? "org-field-input--error" : ""
                  }`}
                  value={form.department_id}
                  onChange={handleDeptChange}
                  onBlur={handleBlur("department_id")}
                  disabled={isLoading}
                >
                  <option value="">Select department</option>
                  {departments.map((d) => (
                    <option key={d.id} value={String(d.id)}>
                      {d.name}
                    </option>
                  ))}
                </select>
              )}
              {errors.department_id && (
                <span className="org-field-error">{errors.department_id}</span>
              )}
            </div>
          </div>

          {/* Site — dropdown, depends on department */}
          <div className="org-form-row" style={{ gridTemplateColumns: "1fr" }}>
            <div className="org-form-field">
              <label className="org-field-label">
                Site <span className="req">*</span>
              </label>
              {!form.department_id ? (
                <select className="org-field-input" disabled>
                  <option>Select a department first</option>
                </select>
              ) : loadingSites ? (
                <select className="org-field-input" disabled>
                  <option>Loading sites…</option>
                </select>
              ) : (
                <select
                  className={`org-field-input ${
                    errors.site_id ? "org-field-input--error" : ""
                  }`}
                  value={form.site_id}
                  onChange={handleChange("site_id")}
                  onBlur={handleBlur("site_id")}
                  disabled={isLoading}
                >
                  <option value="">Select site</option>
                  {sites.map((s) => (
                    <option key={s.site_id} value={String(s.site_id)}>
                      {s.site_name}
                    </option>
                  ))}
                </select>
              )}
              {errors.site_id && (
                <span className="org-field-error">{errors.site_id}</span>
              )}
            </div>
          </div>

          {/* Fuel Type — dropdown */}
          <div className="org-form-row" style={{ gridTemplateColumns: "1fr" }}>
            <div className="org-form-field">
              <label className="org-field-label">
                Fuel Type <span className="req">*</span>
              </label>
              {loadingList ? (
                <select className="org-field-input" disabled>
                  <option>Loading fuel types…</option>
                </select>
              ) : (
                <select
                  className={`org-field-input ${
                    errors.fuel_type_id ? "org-field-input--error" : ""
                  }`}
                  value={form.fuel_type_id}
                  onChange={handleFuelChange}
                  onBlur={handleBlur("fuel_type_id")}
                  disabled={isLoading}
                >
                  <option value="">Select fuel type</option>
                  {fuelTypes.map((f) => (
                    <option key={f.id} value={String(f.id)}>
                      {f.name} ({f.unit})
                    </option>
                  ))}
                </select>
              )}
              {errors.fuel_type_id && (
                <span className="org-field-error">{errors.fuel_type_id}</span>
              )}
            </div>
          </div>

          {/* Quantity */}
          <div className="org-form-row" style={{ gridTemplateColumns: "1fr" }}>
            <div className="org-form-field">
              <label className="org-field-label">
                Quantity <span className="req">*</span>
              </label>
              <input
                className={`org-field-input ${
                  errors.quantity ? "org-field-input--error" : ""
                }`}
                type="number"
                placeholder="e.g. 5000"
                value={form.quantity}
                onChange={handleQuantityChange}
                onBlur={handleBlur("quantity")}
                onKeyDown={(e) => {
                  if (e.key === ".") e.preventDefault();
                }}
                disabled={isLoading}
                min="1"
              />
              {errors.quantity && (
                <span className="org-field-error">{errors.quantity}</span>
              )}
            </div>
          </div>

          {/* Emission Factor + tCO₂e — shown only after fuel type selected, readonly */}
          {showEfAndTco2e && (
            <div
              className="org-form-row"
              style={{ gridTemplateColumns: "1fr 1fr" }}
            >
              <div className="org-form-field">
                <label className="org-field-label">Emission Factor</label>
                <input
                  className="org-field-input"
                  type="number"
                  value={form.emission_factor}
                  readOnly
                  disabled
                />
              </div>
              <div className="org-form-field">
                <label className="org-field-label">tCO₂e (auto)</label>
                <input
                  className="org-field-input"
                  type="number"
                  value={form.tco2e}
                  readOnly
                  disabled
                />
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {apiError && <span className="org-save-error">✕ {apiError}</span>}
          <button
            className="org-btn-cancel"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="org-btn-save"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? "Saving…" : isEdit ? "Update" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Scope1EmissionModal;
